import type { LiteratureEntry } from '../types/siteEvidence';

export type LiteraturePublication = NonNullable<LiteratureEntry['publication']>;
type PublicationStatus = NonNullable<LiteraturePublication['status']>;

const STATUS_PRIORITY: Record<PublicationStatus, number> = {
  unknown: 0,
  notice: 1,
  'expression-of-concern': 2,
  withdrawn: 3,
  retracted: 4,
};

/** Conservative fallback for older cached records and incomplete provider metadata.
 * A paper studying retractions is not itself retracted. Match editorial labels or
 * an explicit sentence about THIS paper, never an isolated keyword in prose.
 */
export function literaturePublicationStatus(entry: LiteratureEntry): PublicationStatus {
  const title = entry.title.trim();
  let inferred: PublicationStatus = 'unknown';
  if (/^(?:\[retracted\](?:\s|$)|retracted(?: article)?\s*[:\-–—])/i.test(title)) inferred = 'retracted';
  else if (/^(?:\[withdrawn\](?:\s|$)|withdrawn\s*[:\-–—])/i.test(title)) inferred = 'withdrawn';
  else if (/^(?:(?:author|publisher|editorial)\s+)?(?:correction|corrigendum|erratum|retraction(?: notice)?)\s*(?:(?:to|of)\s*)?:/i.test(title)) inferred = 'notice';
  else if (/^(?:editorial\s+)?expression of concern\s*(?::|for\b|about\b)/i.test(title)) inferred = 'expression-of-concern';

  const text = (entry.abstract?.trim() || entry.abstractSnippet || '')
    .replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  // The publisher wording below also occurs on removed Research Square preprints.
  const declaration = /(?:^|[.!?]\s+)(?:abstract\s*:?\s*)?(?:this (?:paper|article|preprint|manuscript|submission) (?:has been|is|was) (retracted|withdrawn|removed)|(?:the authors?|research square|the publisher) (?:have|has) (retracted|withdrawn|removed) this (?:paper|article|preprint|manuscript|submission))\b/i.exec(text);
  const action = declaration?.[1] ?? declaration?.[2];
  const textStatus: PublicationStatus = action?.toLowerCase() === 'retracted' ? 'retracted' : action ? 'withdrawn' : 'unknown';
  const explicit = entry.publication?.status ?? 'unknown';
  return [explicit, inferred, textStatus].reduce((a, b) => STATUS_PRIORITY[b] > STATUS_PRIORITY[a] ? b : a);
}

/** Excluded records remain inspectable metadata, but cannot substantiate a report claim. */
export function literaturePublicationExclusion(entry: LiteratureEntry): string | undefined {
  const status = literaturePublicationStatus(entry);
  switch (status) {
    case 'retracted': return 'Retracted publication';
    case 'withdrawn': return 'Withdrawn or removed publication';
    case 'notice': return 'Publication notice, not a research study';
    case 'expression-of-concern': return 'Expression of concern recorded';
    default: return undefined;
  }
}

/** Display form, not a claim that publication or peer review has been verified. */
export function literaturePublicationLabel(entry: LiteratureEntry): string {
  const kind = entry.publication?.kind ?? 'unknown';
  const names: Record<LiteraturePublication['kind'], string> = {
    'journal-article': 'Journal article',
    'conference-paper': 'Conference paper',
    preprint: 'Preprint; contextual evidence',
    'posted-content': 'Posted content; publication status unverified',
    other: 'Other publication',
    unknown: 'Publication status unknown',
  };
  const exclusion = literaturePublicationExclusion(entry);
  const correction = entry.publication?.notices?.some(n => /^(?:correction|erratum|corrigendum)$/i.test(n.type));
  return `${names[kind]}${exclusion ? `; ${exclusion.toLowerCase()}` : correction ? '; correction recorded' : ''}`;
}

/** Preserve integrity evidence when another provider/version supplies the display copy. */
export function mergeLiteraturePublication(
  survivor: LiteratureEntry,
  copies: ReadonlyArray<LiteratureEntry>,
): LiteratureEntry {
  const status = copies.map(literaturePublicationStatus)
    .reduce((a, b) => STATUS_PRIORITY[b] > STATUS_PRIORITY[a] ? b : a, 'unknown');
  const notices = [...new Map(copies.flatMap(e => e.publication?.notices ?? [])
    .map(n => [`${n.type}:${n.doi ?? ''}`, n])).values()]
    .sort((a, b) => a.type.localeCompare(b.type) || (a.doi ?? '').localeCompare(b.doi ?? ''));
  const relatedDois = [...new Set(copies.flatMap(e => e.publication?.relatedDois ?? []))].sort();
  const retrievalQueries = [...new Set(copies.flatMap(e => e.retrievalQueries ?? []))].sort();
  const hasMetadata = copies.some(e => e.publication) || status !== 'unknown';
  const kind = survivor.publication?.kind && survivor.publication.kind !== 'unknown' ? survivor.publication.kind
    : copies.find(e => e.publication?.kind && e.publication.kind !== 'unknown')?.publication?.kind ?? 'unknown';
  const doiKey = (doi?: string) => (doi ?? '').trim().toLowerCase().replace(/^https?:\/\/(?:dx\.)?doi\.org\//, '');
  const isDraft = (e: LiteratureEntry) => e.provider === 'arxiv'
    || e.publication?.kind === 'preprint' || e.publication?.kind === 'posted-content';
  const sameVersion = copies.filter(e => {
    // arXiv can carry its journal counterpart's DOI, but its abstract is still
    // a distinct version. Do not relabel that text as the journal's abstract.
    if (isDraft(e) !== isDraft(survivor)) return false;
    if (e.provider === 'arxiv' && survivor.provider === 'arxiv' && e.identifier !== survivor.identifier) return false;
    const doi = doiKey(survivor.doi);
    return (doi.startsWith('10.') && doi.includes('/') && doi === doiKey(e.doi))
      || (e.provider === survivor.provider && e.identifier === survivor.identifier);
  }).sort((a, b) => (b.abstract?.trim().length ?? 0) - (a.abstract?.trim().length ?? 0)
    || b.abstractSnippet.trim().length - a.abstractSnippet.trim().length
    || a.id.localeCompare(b.id));
  // Fill holes only from the same record/version. Never present a distinct
  // preprint's abstract as text from its journal counterpart.
  const abstract = survivor.abstract?.trim() ? survivor.abstract : sameVersion.find(e => e.abstract?.trim())?.abstract;
  const abstractSnippet = survivor.abstractSnippet.trim() ? survivor.abstractSnippet
    : (abstract?.trim() || sameVersion.find(e => e.abstractSnippet.trim())?.abstractSnippet || '').slice(0, 280);
  return {
    ...survivor,
    ...(abstract !== undefined ? { abstract } : {}),
    abstractSnippet,
    ...(hasMetadata ? { publication: {
      ...survivor.publication,
      kind,
      status,
      ...(notices.length ? { notices } : {}),
      ...(relatedDois.length ? { relatedDois } : {}),
    } } : {}),
    ...(retrievalQueries.length ? { retrievalQueries } : {}),
  };
}
